/**
 * SetupTab — Admin → Setup: the guided workspace-setup surface.
 *
 * Three sections, each real and persisted today:
 *
 *  1. Company-brain walkthrough — a four-step wizard (voice → positioning →
 *     offers → ICP). Every save upserts deterministic Brand Brain entries
 *     (shared with the Company Brain editor), so finishing the walkthrough
 *     IS a populated brain that immediately feeds content / proposal /
 *     blog / email generation.
 *
 *  2. Org KPIs & targets — a small editable grid persisted as one
 *     org-scoped row; the operating targets the team steers by.
 *
 *  3. Governance — how agent autonomy works here (tiers link to the live
 *     surfaces), plus SLA and process notes for the team.
 *
 * All decisions are the PURE model in src/lib/setupWalkthrough.ts (tested);
 * this file is read → edit → validate → persist → toast, nothing clever.
 */

import { useEffect, useState } from 'react';
import {
  ArrowRight,
  Bot,
  Check,
  CircleDashed,
  ClipboardList,
  Compass,
  History,
  Plus,
  Save,
  ShieldCheck,
  Target,
  Trash2,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useToast } from './ToastProvider';
import { useAuth } from '../lib/auth/AuthProvider';
import { C } from '../lib/tokens';
import { emptyBrandBrain, type BrandBrain } from '../lib/brandBrain/schema';
import { loadBrandBrain, saveBrandBrain } from '../lib/brandBrain/persist';
import {
  SETUP_STEPS,
  applyWalkthroughStep,
  emptyGovernanceNotes,
  filledKpis,
  makeOrgKpi,
  setupStepMeta,
  stepValuesFromBrain,
  validateKpis,
  validateStepText,
  walkthroughState,
  KPI_CADENCES,
  type GovernanceNotes,
  type KpiCadence,
  type OrgKpi,
  type SetupStepKey,
} from '../lib/setupWalkthrough';
import {
  loadGovernanceNotes,
  loadOrgKpis,
  saveGovernanceNotes,
  saveOrgKpis,
} from '../lib/setupPersist';

export interface SetupTabProps {
  /** Switch AdminView to the Company Brain editor tab. */
  onOpenBrain: () => void;
  /** Switch AdminView to the Audit log tab (approvals + sealed ledger). */
  onOpenAudit: () => void;
  /** Navigate to Dev → Agents (autonomy tiers live there). Optional so the
   *  panel degrades to plain directions when the host can't navigate. */
  onOpenAgents?: () => void;
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.04)',
  color: C.text,
  fontSize: 13,
  width: '100%',
};

export function SetupTab({ onOpenBrain, onOpenAudit, onOpenAgents }: SetupTabProps) {
  const { user } = useAuth();
  const { push } = useToast();
  const updatedBy = user?.displayName || user?.email || 'operator';

  // --- Walkthrough state ---------------------------------------------------
  const [brain, setBrain] = useState<BrandBrain>(emptyBrandBrain);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<SetupStepKey>('voice');
  const [draft, setDraft] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);
  const [savingStep, setSavingStep] = useState(false);

  // --- KPI state -----------------------------------------------------------
  const [kpis, setKpis] = useState<OrgKpi[]>([makeOrgKpi()]);
  const [kpisDirty, setKpisDirty] = useState(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [savingKpis, setSavingKpis] = useState(false);
  const [savedKpiCount, setSavedKpiCount] = useState(0);

  // --- Governance state ----------------------------------------------------
  const [notes, setNotes] = useState<GovernanceNotes>(emptyGovernanceNotes);
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Load everything on mount; each loader degrades to its honest empty.
  useEffect(() => {
    let active = true;
    Promise.all([loadBrandBrain(), loadOrgKpis(), loadGovernanceNotes()])
      .then(([loadedBrain, loadedKpis, loadedNotes]) => {
        if (!active) return;
        setBrain(loadedBrain);
        const st = walkthroughState(loadedBrain);
        const key = st.nextKey ?? 'voice';
        setActiveKey(key);
        setDraft(stepValuesFromBrain(loadedBrain)[key]);
        setKpis(loadedKpis.length > 0 ? loadedKpis : [makeOrgKpi()]);
        setSavedKpiCount(loadedKpis.length);
        setNotes(loadedNotes);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const state = walkthroughState(brain);
  const values = stepValuesFromBrain(brain);
  const meta = setupStepMeta(activeKey);
  const stepIndex = SETUP_STEPS.findIndex((s) => s.key === activeKey);

  const selectStep = (key: SetupStepKey) => {
    setActiveKey(key);
    setDraft(values[key]);
    setStepError(null);
  };

  const saveStep = async () => {
    const err = validateStepText(activeKey, draft);
    if (err) {
      setStepError(err);
      return;
    }
    const next = applyWalkthroughStep(brain, activeKey, draft, {
      updatedBy,
      now: new Date().toISOString(),
    });
    setSavingStep(true);
    try {
      await saveBrandBrain(next, updatedBy);
      setBrain(next);
      setStepError(null);
      const nextState = walkthroughState(next);
      push({
        kind: 'success',
        text: `${meta.label} saved to the company brain.`,
        detail: nextState.complete
          ? 'All four steps done — content and proposals now compose from your brain.'
          : `${nextState.doneCount} of ${nextState.total} steps done.`,
      });
      if (nextState.nextKey) {
        setActiveKey(nextState.nextKey);
        setDraft(stepValuesFromBrain(next)[nextState.nextKey]);
      }
    } catch (e) {
      push({
        kind: 'error',
        text: 'Could not save this step.',
        detail: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSavingStep(false);
    }
  };

  const setKpiField = (id: string, patch: Partial<OrgKpi>) => {
    setKpis((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setKpisDirty(true);
    setKpiError(null);
  };

  const saveKpis = async () => {
    const err = validateKpis(kpis);
    if (err) {
      setKpiError(err);
      return;
    }
    const keep = filledKpis(kpis);
    setSavingKpis(true);
    try {
      await saveOrgKpis(keep, updatedBy);
      setKpis(keep.length > 0 ? keep : [makeOrgKpi()]);
      setSavedKpiCount(keep.length);
      setKpisDirty(false);
      push({
        kind: 'success',
        text: 'KPIs saved.',
        detail:
          keep.length > 0
            ? `${keep.length} target${keep.length === 1 ? '' : 's'} on record for the org.`
            : 'No targets on record — the board is honestly empty.',
      });
    } catch (e) {
      push({
        kind: 'error',
        text: 'Could not save KPIs.',
        detail: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSavingKpis(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await saveGovernanceNotes(
        { sla: notes.sla.trim(), process: notes.process.trim() },
        updatedBy,
      );
      setNotesDirty(false);
      push({ kind: 'success', text: 'Governance notes saved.' });
    } catch (e) {
      push({
        kind: 'error',
        text: 'Could not save governance notes.',
        detail: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── Header: progress ─────────────────────────────────────────── */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,0,255,0.28)',
              color: C.magentaSoft,
              flexShrink: 0,
            }}
          >
            <Compass size={19} strokeWidth={1.75} />
          </span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 className="t-section-title" style={{ margin: 0 }}>Guided setup</h2>
            <p className="t-meta" style={{ margin: '3px 0 0' }}>
              Teach ARGO your company once — voice, positioning, offers, ICP — then set the
              targets and ground rules the whole workspace runs on.
            </p>
          </div>
          <div style={{ minWidth: 180 }}>
            <div className="t-meta" style={{ marginBottom: 6, textAlign: 'right' }}>
              {state.doneCount} of {state.total} brain steps · {state.percent}%
            </div>
            <div
              role="progressbar"
              aria-valuenow={state.percent}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{
                height: 6,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${state.percent}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: state.complete
                    ? 'linear-gradient(90deg, rgba(80,220,140,0.9), rgba(80,220,140,0.6))'
                    : 'linear-gradient(90deg, rgba(255,0,255,0.8), rgba(187,65,152,0.6))',
                  transition: 'width 240ms ease',
                }}
              />
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* ── 1. Company-brain walkthrough ─────────────────────────────── */}
      <GlassPanel animate={false} style={{ padding: '18px 20px' }}>
        {loading ? (
          <p className="t-meta" style={{ margin: 0 }}>Loading your setup…</p>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <h3 className="t-section-title" style={{ margin: 0 }}>
                1 · Company brain walkthrough
              </h3>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                Four short answers. Each one is saved into the Brand Brain and immediately
                shapes every generated blog, email, and proposal.
              </p>
            </div>

            {/* Step pills */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {state.steps.map((s, i) => {
                const active = s.key === activeKey;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => selectStep(s.key)}
                    className="glass-btn"
                    style={{
                      padding: '5px 12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      border: `1px solid ${
                        active
                          ? 'rgba(255,0,255,0.45)'
                          : s.done
                            ? 'rgba(80,220,140,0.35)'
                            : 'rgba(255,255,255,0.10)'
                      }`,
                      background: active
                        ? 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))'
                        : 'transparent',
                      color: active ? '#fff' : s.done ? C.green : C.textMuted,
                    }}
                  >
                    {s.done ? <Check size={12} /> : <CircleDashed size={12} />}
                    {i + 1}. {s.label}
                  </button>
                );
              })}
            </div>

            {/* Active step */}
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="setup-step-input" style={{ display: 'grid', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  Step {stepIndex + 1} of {SETUP_STEPS.length} — {meta.question}
                </span>
                <span className="t-meta">Why it matters: {meta.feeds}</span>
              </label>
              <textarea
                id="setup-step-input"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setStepError(null);
                }}
                rows={4}
                placeholder={`e.g. ${meta.example}`}
                style={{
                  width: '100%',
                  resize: 'vertical',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${stepError ? 'rgba(255,80,80,0.55)' : 'rgba(255,255,255,0.12)'}`,
                  background: 'rgba(255,255,255,0.04)',
                  color: C.text,
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              />
              {stepError && (
                <span role="alert" style={{ color: '#ff6b6b', fontSize: 12 }}>
                  {stepError}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void saveStep()}
                  disabled={savingStep}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    opacity: savingStep ? 0.6 : 1,
                  }}
                >
                  {savingStep ? 'Saving…' : 'Save & continue'}
                  <ArrowRight size={14} />
                </button>
                <span className="t-meta">
                  {values[activeKey].trim()
                    ? 'This step is saved — editing overwrites the same brain entry.'
                    : 'Not saved yet.'}
                </span>
              </div>
            </div>

            {state.complete && (
              <div
                role="status"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: '1px solid rgba(80,220,140,0.35)',
                  background: 'rgba(80,220,140,0.08)',
                  flexWrap: 'wrap',
                }}
              >
                <Check size={15} style={{ color: C.green, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: C.text, flex: 1, minWidth: 200 }}>
                  Company brain is live — content, proposals, blogs, and email now compose
                  from these answers.
                </span>
                <button
                  type="button"
                  className="glass-btn"
                  onClick={onOpenBrain}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Fine-tune in Company Brain →
                </button>
              </div>
            )}
          </div>
        )}
      </GlassPanel>

      {/* ── 2. Org KPIs & targets ────────────────────────────────────── */}
      <GlassPanel animate={false} style={{ padding: '18px 20px' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Target size={16} style={{ color: C.textMuted, marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h3 className="t-section-title" style={{ margin: 0 }}>2 · Org KPIs & targets</h3>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                The numbers the org steers by. Stored in the company brain as the org-wide
                reference — Goals tracks execution; this is the target board.
              </p>
            </div>
            {savedKpiCount > 0 && (
              <span className="glass-pill glass-pill--green" style={{ fontSize: 11 }}>
                {savedKpiCount} on record
              </span>
            )}
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {/* Column headers */}
            <div
              className="t-meta"
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1.2fr 1fr 110px 1fr 30px',
                gap: 8,
                padding: '0 2px',
              }}
            >
              <span>KPI</span>
              <span>How it's measured</span>
              <span>Target</span>
              <span>Cadence</span>
              <span>Owner</span>
              <span />
            </div>
            {kpis.map((k) => (
              <div
                key={k.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1.2fr 1fr 110px 1fr 30px',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <input
                  aria-label="KPI name"
                  value={k.name}
                  onChange={(e) => setKpiField(k.id, { name: e.target.value })}
                  placeholder="Qualified pipeline"
                  style={inputStyle}
                />
                <input
                  aria-label="KPI metric"
                  value={k.metric}
                  onChange={(e) => setKpiField(k.id, { metric: e.target.value })}
                  placeholder="$ of stage-2+ opps"
                  style={inputStyle}
                />
                <input
                  aria-label="KPI target"
                  value={k.target}
                  onChange={(e) => setKpiField(k.id, { target: e.target.value })}
                  placeholder="$250k / qtr"
                  style={inputStyle}
                />
                <select
                  aria-label="KPI cadence"
                  value={k.cadence}
                  onChange={(e) => setKpiField(k.id, { cadence: e.target.value as KpiCadence })}
                  style={{ ...inputStyle, padding: '8px 6px' }}
                >
                  {KPI_CADENCES.map((c) => (
                    <option key={c} value={c} style={{ color: '#000' }}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="KPI owner"
                  value={k.owner}
                  onChange={(e) => setKpiField(k.id, { owner: e.target.value })}
                  placeholder="Who owns it"
                  style={inputStyle}
                />
                <button
                  type="button"
                  aria-label="Remove KPI row"
                  className="glass-btn"
                  onClick={() => {
                    setKpis((rows) => {
                      const next = rows.filter((r) => r.id !== k.id);
                      return next.length > 0 ? next : [makeOrgKpi()];
                    });
                    setKpisDirty(true);
                    setKpiError(null);
                  }}
                  style={{ padding: 6, color: C.textMuted }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          {kpiError && (
            <span role="alert" style={{ color: '#ff6b6b', fontSize: 12 }}>
              {kpiError}
            </span>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="glass-btn"
              onClick={() => setKpis((rows) => [...rows, makeOrgKpi()])}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 12 }}
            >
              <Plus size={13} /> Add KPI
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={() => void saveKpis()}
              disabled={savingKpis || !kpisDirty}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 14px',
                opacity: savingKpis || !kpisDirty ? 0.6 : 1,
              }}
            >
              <Save size={13} />
              {savingKpis ? 'Saving…' : 'Save targets'}
            </button>
            <span className="t-meta">
              {kpisDirty ? 'Unsaved changes.' : 'Blank rows are ignored on save.'}
            </span>
          </div>
        </div>
      </GlassPanel>

      {/* ── 3. Governance: autonomy, SLAs, process ───────────────────── */}
      <GlassPanel animate={false} style={{ padding: '18px 20px' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <ShieldCheck size={16} style={{ color: C.textMuted, marginTop: 2, flexShrink: 0 }} />
            <div>
              <h3 className="t-section-title" style={{ margin: 0 }}>3 · Governance</h3>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                How autonomous work is allowed to run here, and the service standards the
                team commits to.
              </p>
            </div>
          </div>

          {/* Autonomy tiers — live surfaces, honestly linked */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(0,0,0,0.18)',
              flexWrap: 'wrap',
            }}
          >
            <Bot size={16} style={{ color: C.textMuted, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="t-card-title" style={{ fontSize: 13 }}>Autonomy tiers</div>
              <div className="t-meta" style={{ color: C.textMuted }}>
                Agents start propose-only; every action class earns auto-run through a clean,
                sealed approval history. Manage agents under Dev → Agents; review every
                decision in the Audit log.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {onOpenAgents && (
                <button
                  type="button"
                  className="glass-btn"
                  onClick={onOpenAgents}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12 }}
                >
                  <Bot size={12} /> Open Agents
                </button>
              )}
              <button
                type="button"
                className="glass-btn"
                onClick={onOpenAudit}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12 }}
              >
                <History size={12} /> Audit log
              </button>
            </div>
          </div>

          {/* SLA + process notes */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <label htmlFor="setup-sla-notes" style={{ display: 'grid', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>SLA notes</span>
                <span className="t-meta">
                  Service commitments the team holds itself to — response times, delivery
                  windows, escalation paths. A shared reference for the org (agents don't
                  enforce these yet).
                </span>
              </label>
              <textarea
                id="setup-sla-notes"
                value={notes.sla}
                onChange={(e) => {
                  setNotes((n) => ({ ...n, sla: e.target.value }));
                  setNotesDirty(true);
                }}
                rows={3}
                placeholder="e.g. First reply < 4 business hours. Proposals out within 3 days of a qualified call."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label htmlFor="setup-process-notes" style={{ display: 'grid', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Process notes</span>
                <span className="t-meta">
                  How work moves here — who approves what, hand-off points, definition of done.
                </span>
              </label>
              <textarea
                id="setup-process-notes"
                value={notes.process}
                onChange={(e) => {
                  setNotes((n) => ({ ...n, process: e.target.value }));
                  setNotesDirty(true);
                }}
                rows={3}
                placeholder="e.g. All agent writes route through the approvals inbox. Content ships only after a human pass."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={() => void saveNotes()}
                disabled={savingNotes || !notesDirty}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 14px',
                  opacity: savingNotes || !notesDirty ? 0.6 : 1,
                }}
              >
                <ClipboardList size={13} />
                {savingNotes ? 'Saving…' : 'Save governance notes'}
              </button>
              <span className="t-meta">{notesDirty ? 'Unsaved changes.' : 'Saved org-wide.'}</span>
            </div>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}
